// React
import PropTypes from "prop-types";
import { useContext, useEffect, useMemo, useState, useCallback } from "react";

// @mui material components
import {
	Autocomplete,
	Card,
	CircularProgress,
	Fade,
	Grid,
	Icon,
	InputLabel,
	MenuItem,
	Modal,
	Select,
	TextField,
	Tooltip,
	Tab,
	Tabs,
	Box,
	Chip,
	Checkbox,
} from "@mui/material";

import { gql, useMutation, useQuery, useLazyQuery } from "@apollo/client";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import { MessageManager } from "context";
import SoftInputNumber from "components/SoftInputNumberUpgrade";
import ModalNuevoTipo from "../../productos/ModalNuevoTIpo";
import DataTable from "components/Tables/DataTable";
import SinDatos from "components/Carteles/SinDatos";
import dataIngredientes from "../data/dataIngredientes";
import { formatMoneyPunto } from "utils/formatters";
import dataDescartables from "../data/dataDescartables";
import { getUproColor } from "utils/colors";
import rgba from "assets/theme/functions/rgba";

const style = {
	position: "absolute",
	top: "50%",
	left: "50%",
	transform: "translate(-50%, -50%)",
	maxHeight: "95%",
	width: { xs: "90%", sm: "80%", xxl: "70%" },
	overflowY: "auto",
	p: 3,
};

const productoInicial = {
	nombre: "",
	idCategoria: "",
	precioCarta: "",
	precioOnline: "",
	ingredientes: [],
	descripcion: "",
	idComanderas: [],
	lugarVenta: [],
	cantidadPorMayor: 0,
	idArea: "",
};

const LUGARES_VENTA = ["APP", "Carta", "Totem"];
export default function ModalProducto({
	open,
	handleClose,
	refetch,
	refetchTipos,
	selectedProducto,
	categorias,
	optionsCreate = {},
	tiposProducto,
}) {
	const [producto, setProducto] = useState(productoInicial);
	const [listadoIngredientes, setListadoIngredientes] = useState([]);
	const [listadoDescartables, setListadoDescartables] = useState([]);
	const [materiaPrima, setMateriaPrima] = useState({
		idMateriaPrima: "",
		idTipoUnidad: "",
		cantidad: "",
	});
	const [descartable, setDescartable] = useState({
		idMateriaPrima: "",
		idTipoUnidad: "",
		cantidad: "",
	});
	const [modalNuevoTipo, setModalNuevoTipo] = useState(false);
	const [idsBorrar, setIdsBorrar] = useState([]);
	const [idsBorrarDescartables, setIdsBorrarDescartables] = useState([]);
	const [camposConError, setCamposConError] = useState(new Set());
	const [mostrarTooltips, setMostrarTooltips] = useState(false);
	const [motivoBloqueoTipoUnidadIngrediente, setMotivoBloqueoTipoUnidadIngrediente] = useState("");
	const { columns, getRows } = dataIngredientes;
	const { columns: columnsDescartables, getRows: getRowsDescartables } = dataDescartables;

	const esIngrediente = optionsCreate?.esIngrediente || selectedProducto?.esIngrediente;

	const handleChangeProducto = useCallback((e) => {
		const { name, value } = e.target;
		const normalizedValue =
			name === "idComanderas"
				? (typeof value === "string" ? value.split(",") : value).map((id) => String(id))
				: value;

		setProducto((prev) => {
			let newProducto = { ...prev, [name]: normalizedValue };

			// Si se selecciona la categoría 16, forzar "APP" en lugarVenta
			if (name === "idCategoria" && value === "16") {
				const currentLugarVenta = Array.isArray(prev.lugarVenta) ? prev.lugarVenta : [];
				if (!currentLugarVenta.includes("APP")) {
					newProducto.lugarVenta = [...currentLugarVenta, "APP"];
				}
			}

			// Si la categoría es 16 y se intenta modificar lugarVenta, forzar que "APP" esté presente
			if (name === "lugarVenta" && (name === "idCategoria" ? value === "16" : prev.idCategoria === "16")) {
				if (!value.includes("APP")) {
					newProducto.lugarVenta = [...value, "APP"];
				}
			}

			return newProducto;
		});
	}, []);

	const { handleSnackbar } = useContext(MessageManager);

	const [saveProducto, { loading: loadingSave }] = useMutation(
		gql`
      mutation saveProducto($input: ProductoInput!) {
        saveProducto(input: $input) {
          id
          categoriaProducto {
            id
            categoria
          }
        }
      }
    `
	);

	const [getPlatos, { loading: loadingPlatos, data: dataPlatos }] = useLazyQuery(
		gql`
      query materiasPrimas {
        materiasPrimas(filter: { tipo: "Plato" }) {
          id
          nombre
          idProductoReferencia
        }
      }
    `
	);

	const [getMateriaPrimaPorProducto, { loading: loadingMateriaPrima, data: dataMateriaPrima }] =
		useLazyQuery(
			gql`
        query materiaPrimaPorProducto($idProducto: ID!) {
          materiaPrimaPorProducto(idProducto: $idProducto) {
            id
            idTipoUnidad
            tipoUnidad {
              id
              nombre
              abreviatura
              multiplicador
            }
            stockPorSede {
              cantidad
            }
            ingredientes {
              id
              producto {
                id
                nombre
              }
            }
          }
        }
      `,
			{
				fetchPolicy: "network-only",
				nextFetchPolicy: "network-only",
			}
		);

	const tipoUnidadIngredienteBloqueado = useMemo(
		() => Boolean(motivoBloqueoTipoUnidadIngrediente),
		[motivoBloqueoTipoUnidadIngrediente]
	);
	const verificandoBloqueoTipoUnidadIngrediente = useMemo(
		() => Boolean(open && selectedProducto?.esIngrediente && loadingMateriaPrima),
		[open, selectedProducto?.esIngrediente, loadingMateriaPrima]
	);

	useEffect(() => {
		getPlatos();
	}, []);

	const handleSave = () => {
		// Validar campos obligatorios y mostrar tooltips si hay errores
		const errores = new Set();

		// Validaciones para ingredientes
		if (esIngrediente) {
			if (!producto.nombre) errores.add('nombre');
			if (!producto.idTipoUnidad) errores.add('idTipoUnidad');
			if (!producto.idArea) errores.add('idArea');
			if (listadoIngredientes?.length == 0) errores.add('ingredientes');
		} else {
			// Validaciones para productos normales
			if (!producto.nombre) errores.add('nombre');
			if (!producto.idCategoria) errores.add('idCategoria');
			if (!producto.idArea) errores.add('idArea');
			if (listadoIngredientes?.length == 0) errores.add('ingredientes');

			if (producto.idCategoria !== "16") {
				if (!Array.isArray(producto.idComanderas) || producto.idComanderas.length === 0) {
					errores.add('idComanderas');
				}
				if (!producto.cantidadPorciones) errores.add('cantidadPorciones');
				if (!producto.precioCarta) errores.add('precioCarta');
				if (!producto.precioOnline) errores.add('precioOnline');

				// Solo validar precios por mayor si cantidadPorMayor > 0
				if (producto.cantidadPorMayor > 0) {
					if (!producto.precioPorMayor) errores.add('precioPorMayor');
					if (!producto.precioPorMayorOnline) errores.add('precioPorMayorOnline');
				}
			}
		}

		// Validación de costos para productos normales (incluye categoría 16)
		if (!esIngrediente && producto.precioCarta) {
			if (costoPorPorcion >= parseFloat(producto.precioCarta)) {
				errores.add('costosuperior');
			}
		}

		// Si hay errores, mostrar tooltips y no guardar
		if (errores.size > 0) {
			setCamposConError(errores);
			setMostrarTooltips(true);

			// Ocultar tooltips después de 5 segundos
			setTimeout(() => {
				setMostrarTooltips(false);
				setCamposConError(new Set());
			}, 5000);

			// Mostrar mensaje de error
			handleSnackbar("Por favor complete todos los campos obligatorios", "error");
			return;
		}

		// Si no hay errores, proceder con el guardado
		// Usar los valores memoizados (misma lógica que UI)
		let precioCostoIngredientes = parseFloat(costoPorPorcion || 0);
		let precioCostoDescartables = parseFloat(costoDescartablesPorUnidad || 0);
		let ingredientes = listadoIngredientes
			.filter((ingrediente) => !ingrediente.idExistente)
			.map((ingrediente) => ({
				idMateriaPrima: ingrediente.idMateriaPrima,
				cantidad: Number(ingrediente.cantidad),
				idTipoUnidad: ingrediente.idTipoUnidad,
			}));

		let descartables = listadoDescartables
			.filter((descartable) => !descartable.idExistente)
			.map((descartable) => ({
				idMateriaPrima: descartable.idMateriaPrima,
				cantidad: Number(descartable.cantidad),
				idTipoUnidad: descartable.idTipoUnidad,
			}));
		const printNamesSeleccionados = (producto.idComanderas || [])
			.map((idComandera) =>
				comanderasDisponibles.find((comandera) => String(comandera.id) === String(idComandera))
			)
			.filter(Boolean)
			.map((comandera) => comandera.printName);
		saveProducto({
			variables: {
				input: {
					id: selectedProducto ? selectedProducto.id : null,
					idCategoria: producto.idCategoria,
					idArea: producto.idArea,
					nombre: producto.nombre,
					descripcion: producto.descripcion,
					precioCarta: producto.precioCarta || null,
					precioOnline: producto.precioOnline || null,
					precioPorMayor: producto.precioPorMayor || null,
					precioPorMayorOnline: producto.precioPorMayorOnline || null,
					cantidadPorMayor: producto.cantidadPorMayor ? parseInt(producto.cantidadPorMayor) : null,
					esElaborado: esIngrediente ? true : producto.esElaborado || false,
					esIngrediente: esIngrediente,
					cantidadPorciones: producto.cantidadPorciones ? parseInt(producto.cantidadPorciones) : 1,
					seccion: printNamesSeleccionados.join(","),
					idComanderas: (producto.idComanderas || []).map((id) => Number(id)),
					lugarVenta: producto.lugarVenta,
					ingredientes: ingredientes,
					costoIngredientes: precioCostoIngredientes + precioCostoDescartables,
					descartables: descartables,
					ingredientesBorrar: idsBorrar,
					descartablesBorrar: idsBorrarDescartables,
					idTipoUnidad: esIngrediente ? producto.idTipoUnidad : null,
					precioCosto: precioCostoIngredientes + precioCostoDescartables,
				},
			},
		})
			.then((data) => {
				handleSnackbar("Producto guardado correctamente", "success");
				refetch();
				handleClose();
			})
			.catch((error) => {
				const errorMessage = error?.graphQLErrors?.[0]?.message || "Error al guardar";
				handleSnackbar(errorMessage, "error");
			});
	};
	const { data: unidades } = useQuery(
		gql`
      query unidades {
        unidades {
          id
          nombre
          abreviatura
          multiplicador
        }
      }
    `
	);

	const [getAreas, { data: areas }] = useLazyQuery(
		gql`
      query areas {
        areaProductos {
          id
          nombre
        }
      }
    `,
		{
			fetchPolicy: "network-only",
			nextFetchPolicy: "network-only",
		}
	);
	const { data: dataComanderas } = useQuery(
		gql`
      query getComanderasModalProducto {
        comanderas(order: { field: "printName", order: ASC }) {
          id
          printName
          habilitado
        }
      }
    `,
		{ fetchPolicy: "network-only", skip: !open }
	);
	const comanderasDisponibles = useMemo(
		() => dataComanderas?.comanderas || [],
		[dataComanderas?.comanderas]
	);
	const {
		data: materiaPrimaData,
		refetch: refetchMateriaPrima,
		loading,
	} = useQuery(
		gql`
      query materiasPrimas($search: String) {
        materiasPrimas(
          search: $search
          pagination: { page: 1, limit: 20 }
          filter: { tipo: ["Consumible", "Plato_Ingrediente"] }
        ) {
          id
          nombre
          precio
          porcentajeUtilizable
          tipo
          categoria
          disabled
          tipoUnidad {
            id
            nombre
            abreviatura
            multiplicador
          }
        }
      }
    `
	);

	const [consumibles, setConsumibles] = useState([]);
	const [materiales, setMateriales] = useState([]);

	useEffect(() => {
		if (materiaPrimaData) {
			let consumibles = materiaPrimaData.materiasPrimas.filter(
				(item) =>
					([
						"Alimentos",
						"Verduras y Frutas",
						"Carnes",
						"Lácteos",
						"Granos y Cereales",
						"Aceites y Grasas",
						"Bebidas",
						"Condimentos",
						"Empaques y Envases",
						"Cafetería",
						"Pastelería",
						"Semillas",
						"Legumbres",
						"Congelados",
						"Panadería",
						"Fiambre",
						"Embutidos",
					].includes(item.categoria) ||
						item.tipo === "Plato_Ingrediente") &&
					item?.disabled === false
			);
			let materiales = materiaPrimaData.materiasPrimas.filter(
				(item) => item.categoria === "Descartables"
			);
			setConsumibles(consumibles);
			setMateriales(materiales);
		}
	}, [materiaPrimaData]);
	const calcularValoresIngrediente = useCallback((ingrediente) => {
		let cantidadIngresada = parseFloat(ingrediente.cantidad) || 0;
		let porcentajeUtilizable = parseFloat(ingrediente.materiaPrima?.porcentajeUtilizable) || 100;
		let precioPorUnidad = parseFloat(ingrediente.materiaPrima?.precio) || 0;

		let unidadCantidad = ingrediente.tipoUnidad;
		let unidadPrecio = ingrediente.tipoUnidadOrigen || unidadCantidad;

		let multiplicadorCantidad = parseFloat(unidadCantidad?.multiplicador) || 1;
		let multiplicadorPrecio =
			parseFloat(ingrediente?.materiaPrima?.tipoUnidad?.multiplicador) ||
			unidadPrecio?.multiplicador ||
			1;
		let cantidadNecesaria = cantidadIngresada / (porcentajeUtilizable / 100);
		let cantidadConvertida = (cantidadNecesaria * multiplicadorCantidad) / multiplicadorPrecio;
		let precioUtilizable = cantidadConvertida * precioPorUnidad;

		return {
			valorUtilizable: cantidadNecesaria,
			cantidadUtilizable: cantidadNecesaria,
			precioUtilizable: precioUtilizable,
			porcentajeUtilizable: porcentajeUtilizable,
		};
	}, []);

	useEffect(() => {
		if (optionsCreate?.esElaborado && !producto.esElaborado) {
			setProducto((prev) => ({
				...prev,
				esElaborado: optionsCreate?.esElaborado === "elaborado",
				esIngrediente: optionsCreate?.esIngrediente,
				cantidadPorciones: optionsCreate?.esIngrediente ? 1 : prev.cantidadPorciones,
			}));
		}
	}, [optionsCreate?.esElaborado, optionsCreate?.esIngrediente]);

	useEffect(() => {
		if (open) {
			getAreas();
		}
	}, [open]);


	useEffect(() => {
		if (!open || !selectedProducto) return;

		const idCategoria = selectedProducto?.categoriaProducto?.id || null;
		let lugarVenta = Array.isArray(selectedProducto.lugarVenta)
			? selectedProducto.lugarVenta
			: selectedProducto.lugarVenta
				? selectedProducto.lugarVenta.split(",")
				: [];

		// Si la categoria es 16, asegurar que "APP" este incluido
		if (String(idCategoria) === "16" && !lugarVenta.includes("APP")) {
			lugarVenta = [...lugarVenta, "APP"];
		}

		const idComanderasDesdeRelacion = (selectedProducto?.comanderas || []).map((comandera) =>
			String(comandera.id)
		);

		setProducto({
			...selectedProducto,
			idCategoria: idCategoria,
			idArea: selectedProducto?.area?.id || "",
			lugarVenta: lugarVenta,
			idComanderas: idComanderasDesdeRelacion,
		});

		if (selectedProducto.esIngrediente) {
			getMateriaPrimaPorProducto({
				variables: { idProducto: selectedProducto.id },
			});
		}

		let listadoExistente = (selectedProducto.ingredientes || []).map((ingrediente) => {
			let valoresCalculados = calcularValoresIngrediente(ingrediente);

			return {
				cantidad: ingrediente.cantidad,
				nombre: ingrediente.materiaPrima.nombre,
				idTipoUnidad: ingrediente.tipoUnidad.id,
				idMateriaPrima: ingrediente.materiaPrima.id,
				tipoUnidad: ingrediente.tipoUnidad,
				tipoUnidadOrigen: ingrediente.materiaPrima?.tipoUnidad,
				precio: ingrediente.materiaPrima?.precio,
				idExistente: ingrediente.id,
				disabled: ingrediente.materiaPrima.disabled,
				...valoresCalculados,
			};
		});

		let descartablesExistente = (selectedProducto?.descartables || []).map((descartable) => {
			return {
				cantidad: descartable.cantidad,
				nombre: descartable.materiaPrima.nombre,
				idTipoUnidad: descartable.tipoUnidad.id,
				idMateriaPrima: descartable.materiaPrima.id,
				tipoUnidad: descartable.tipoUnidad,
				tipoUnidadOrigen: descartable.materiaPrima?.tipoUnidad,
				precio: descartable.materiaPrima?.precio,
				idExistente: descartable.id,
			};
		});

		setListadoIngredientes(listadoExistente);
		setListadoDescartables(descartablesExistente);
	}, [open, selectedProducto?.id, calcularValoresIngrediente, getMateriaPrimaPorProducto]);

	useEffect(() => {
		if (open) return;

		setProducto(productoInicial);
		setListadoIngredientes([]);
		setListadoDescartables([]);
		setIdsBorrar([]);
		setIdsBorrarDescartables([]);
		setCamposConError(new Set());
		setMostrarTooltips(false);
		setMotivoBloqueoTipoUnidadIngrediente("");
	}, [open]);

	useEffect(() => {
		const idTipoUnidadMateriaPrima = dataMateriaPrima?.materiaPrimaPorProducto?.idTipoUnidad;
		if (!idTipoUnidadMateriaPrima || !selectedProducto?.esIngrediente) return;

		setProducto((prev) => {
			if (prev.idTipoUnidad === idTipoUnidadMateriaPrima) return prev;
			return {
				...prev,
				idTipoUnidad: idTipoUnidadMateriaPrima,
			};
		});
	}, [dataMateriaPrima?.materiaPrimaPorProducto?.idTipoUnidad, selectedProducto?.esIngrediente]);

	useEffect(() => {
		if (!open || !selectedProducto?.esIngrediente) {
			setMotivoBloqueoTipoUnidadIngrediente("");
			return;
		}

		const materiaPrimaIngrediente = dataMateriaPrima?.materiaPrimaPorProducto;
		if (!materiaPrimaIngrediente) {
			setMotivoBloqueoTipoUnidadIngrediente("");
			return;
		}

		const tieneStock = (materiaPrimaIngrediente.stockPorSede || []).some(
			(stock) => Number(stock?.cantidad || 0) > 0
		);
		if (tieneStock) {
			setMotivoBloqueoTipoUnidadIngrediente(
				"No se puede editar el tipo de unidad porque el producto ingrediente ya tiene stock."
			);
			return;
		}

		const usoEnOtrasRecetas = (materiaPrimaIngrediente.ingredientes || []).filter(
			(ingrediente) => Number(ingrediente?.producto?.id) !== Number(selectedProducto?.id)
		);
		if (usoEnOtrasRecetas.length > 0) {
			const nombreProducto = usoEnOtrasRecetas[0]?.producto?.nombre || "otro producto";
			setMotivoBloqueoTipoUnidadIngrediente(
				`No se puede editar el tipo de unidad porque este ingrediente se usa en la receta de "${nombreProducto}".`
			);
			return;
		}

		setMotivoBloqueoTipoUnidadIngrediente("");
	}, [open, selectedProducto?.id, selectedProducto?.esIngrediente, dataMateriaPrima?.materiaPrimaPorProducto]);

	// Limpiar precios por mayor cuando cantidadPorMayor es 0
	useEffect(() => {
		if (
			producto.cantidadPorMayor == 0 &&
			(producto.precioPorMayor || producto.precioPorMayorOnline)
		) {
			setProducto(prev => ({
				...prev,
				precioPorMayor: "",
				precioPorMayorOnline: "",
			}));
		}
	}, [producto.cantidadPorMayor]);

	const ejecutarAccion = useCallback((accion, item) => {
		if (accion === "borrar") {
			let newIngredientes = listadoIngredientes.filter(
				(ingrediente) => ingrediente.nombre !== item.nombre
			);
			if (item.idExistente) {
				setIdsBorrar(prev => [...prev, item.idExistente]);
			}
			setListadoIngredientes(newIngredientes);
		}
	}, [listadoIngredientes]);

	const ejecutarAccionDescartable = useCallback((accion, item) => {
		if (accion === "borrar") {
			let newDescartables = listadoDescartables.filter(
				(descartable) => descartable.nombre !== item.nombre
			);
			if (item.idExistente) {
				setIdsBorrarDescartables(prev => [...prev, item.idExistente]);
			}
			setListadoDescartables(newDescartables);
		}
	}, [listadoDescartables]);

	const allRows = useMemo(() => {
		return getRows(listadoIngredientes, ejecutarAccion);
	}, [listadoIngredientes]);

	const allRowsDescartables = useMemo(() => {
		return getRowsDescartables(listadoDescartables, ejecutarAccionDescartable);
	}, [listadoDescartables]);

	const handleAddDescartable = useCallback(() => {
		if (!descartable || !descartable.nombre) return; // Evita errores si el descartable no tiene nombre

		const index = listadoDescartables.findIndex((item) => item.nombre === descartable.nombre);

		if (index !== -1) {
			// Si el descartable ya existe, actualizarlo
			const descartableExistente = listadoDescartables[index];
			if (descartableExistente.idExistente) {
				setIdsBorrarDescartables((prev) => [...prev, descartableExistente.idExistente]);
			}

			const newDescartables = [...listadoDescartables];
			newDescartables[index] = {
				...descartable,
				cantidad: descartable.cantidad,
				tipoUnidad: descartable.tipoUnidad,
				tipoUnidadOrigen: descartable.tipoUnidadOrigen || descartable.tipoUnidad,
				precio: descartable.precio,
				idExistente: null,
			};

			setListadoDescartables(newDescartables);
		} else {
			// Si el descartable no existe, agregarlo al listado
			setListadoDescartables((prev) => [
				...prev,
				{
					...descartable,
					idTipoUnidad: descartable?.idTipoUnidad,
					tipoUnidadOrigen: descartable.tipoUnidadOrigen || descartable.tipoUnidad,
					precio: descartable.precio,
				},
			]);
		}

		// Reiniciar el estado del descartable
		setDescartable({
			idMateriaPrima: "",
			idTipoUnidad: "",
			cantidad: "",
		});
	}, [descartable, listadoDescartables]);

	const handleAddMateriaPrima = useCallback(() => {
		let ingrediente = listadoIngredientes.find(
			(ingrediente) => ingrediente.nombre == materiaPrima.nombre
		);

		let cantidadIngresada = parseFloat(materiaPrima.cantidad) || 0; // Cantidad ingresada
		let porcentajeUtilizable = parseFloat(materiaPrima.porcentajeUtilizable) || 100; // % de utilidad
		let precioPorUnidad = parseFloat(materiaPrima.precio) || 0; // Precio en la unidad base

		let unidadCantidad = materiaPrima.tipoUnidad; // Unidad en la que se ingresó la cantidad
		let unidadPrecio = materiaPrima.tipoUnidadOrigen || unidadCantidad; // Unidad en la que está expresado el precio

		let multiplicadorCantidad = parseFloat(unidadCantidad?.multiplicador) || 1; // Multiplicador de la cantidad
		let multiplicadorPrecio =
			parseFloat(ingrediente?.materiaPrima?.tipoUnidad?.multiplicador) ||
			unidadPrecio?.multiplicador ||
			1; // Multiplicador del precio

		// ✅ 1. Determinar cuánto se necesita comprar realmente (en la unidad ingresada)
		let cantidadNecesaria = cantidadIngresada / (porcentajeUtilizable / 100);

		// ✅ 2. Convertir la cantidad necesaria a la unidad del precio
		let cantidadConvertida = (cantidadNecesaria * multiplicadorCantidad) / multiplicadorPrecio;

		// ✅ 3. Calcular el precio correcto
		let precioUtilizable = cantidadConvertida * precioPorUnidad;

		if (ingrediente) {
			if (ingrediente.idExistente) {
				setIdsBorrar(prev => [...prev, ingrediente.idExistente]);
			}
			let index = listadoIngredientes.findIndex((item) => item.nombre === materiaPrima.nombre);
			let newIngredientes = [...listadoIngredientes];
			newIngredientes[index] = {
				...ingrediente,
				idTipoUnidad: materiaPrima.tipoUnidad?.id,
				cantidad: materiaPrima.cantidad,
				disabled: materiaPrima.disabled,
				tipoUnidad: materiaPrima.tipoUnidad,
				tipoUnidadOrigen: materiaPrima.tipoUnidadOrigen || materiaPrima.tipoUnidad,
				precio: materiaPrima.precio,
				valorUtilizable: cantidadNecesaria,
				cantidadUtilizable: cantidadNecesaria,
				precioUtilizable: precioUtilizable,
				idExistente: null,
				porcentajeUtilizable: porcentajeUtilizable,
			};
			setListadoIngredientes(newIngredientes);
		} else {
			let newIngredientes = [
				...listadoIngredientes,
				{
					...materiaPrima,
					idTipoUnidad: materiaPrima?.idTipoUnidad,
					tipoUnidadOrigen: materiaPrima.tipoUnidadOrigen || materiaPrima.tipoUnidad,
					precio: materiaPrima.precio,
					valorUtilizable: cantidadNecesaria,
					cantidadUtilizable: cantidadNecesaria,
					precioUtilizable: precioUtilizable,
					porcentajeUtilizable: porcentajeUtilizable,
				},
			];
			setListadoIngredientes(newIngredientes);
		}

		setMateriaPrima({
			idMateriaPrima: "",
			idTipoUnidad: "",
			cantidad: "",
		});
	}, [materiaPrima, listadoIngredientes]);

	const handleSearchChange = useCallback((e) => {
		refetchMateriaPrima({
			search: e?.target?.value || "",
		});
	}, [refetchMateriaPrima]);

	const familias = useMemo(() => ({
		peso: [2, 3], // Gramo, Kilogramo
		volumen: [5, 6, 7], // Litro, Mililitro, Centímetro cúbico
		longitud: [9, 10], // Metro, Centímetro
		unidad: [1], // Unidad (u)
	}), []);

	const obtenerFamilia = useCallback((idUnidad) => {
		idUnidad = Number(idUnidad); // Asegurar que sea número
		for (const [familia, ids] of Object.entries(familias)) {
			if (ids.includes(idUnidad)) return familia;
		}
		return null;
	}, [familias]);

	const [unidadesFiltradas, setUnidadesFiltradas] = useState([]);

	useEffect(() => {
		if (materiaPrima?.unidadOriginal?.id && unidades?.unidades) {
			const idUnidadOriginal = Number(materiaPrima.unidadOriginal.id); // Convertir a número
			const familiaUnidadOriginal = obtenerFamilia(idUnidadOriginal);

			const filtradas = unidades.unidades.filter(
				(unidad) => obtenerFamilia(Number(unidad.id)) === familiaUnidadOriginal
			);

			setUnidadesFiltradas(filtradas);
		} else {
			setUnidadesFiltradas([]);
		}
	}, [materiaPrima?.unidadOriginal, unidades, obtenerFamilia]);

	const isPrecioValido = useCallback((valor) => {
		return (
			valor !== null &&
			valor !== undefined &&
			valor !== "" &&
			!isNaN(Number(valor)) &&
			Number(valor) > 0
		);
	}, []);

	const costoTotalIngredientes = useMemo(() => {
		return listadoIngredientes.reduce((acc, item) => acc + item.precioUtilizable, 0);
	}, [listadoIngredientes]);

	const costoPorPorcion = useMemo(() => {
		return parseFloat(costoTotalIngredientes / (producto?.cantidadPorciones || 1));
	}, [costoTotalIngredientes, producto?.cantidadPorciones]);

	// Costo de descartables total y por unidad
	const costoTotalDescartables = useMemo(() => {
		return parseFloat(
			listadoDescartables.reduce((acc, item) => {
				let cantidadConvertida = parseFloat(item.cantidad) || 0;
				if (item?.tipoUnidadOrigen?.id && item?.tipoUnidad?.id && item.tipoUnidadOrigen?.id !== item.tipoUnidad?.id) {
					const multiplicadorOrigen = parseFloat(item.tipoUnidadOrigen?.multiplicador) || 1;
					const multiplicadorDestino = parseFloat(item.tipoUnidad?.multiplicador) || 1;
					cantidadConvertida = (cantidadConvertida * multiplicadorDestino) / multiplicadorOrigen;
				}
				const precio = parseFloat(item.precio) || 0;
				return acc + precio * cantidadConvertida;
			}, 0)
		) || 0;
	}, [listadoDescartables]);

	// En el modelo actual, los descartables se cargan por unidad del producto,
	// por lo que el costo por unidad de descartables es el total ingresado.
	const costoDescartablesPorUnidad = useMemo(() => {
		return parseFloat(costoTotalDescartables || 0);
	}, [costoTotalDescartables]);

	const costoTotalPorUnidad = useMemo(() => {
		return (costoPorPorcion || 0) + (costoTotalDescartables || 0);
	}, [costoPorPorcion, costoTotalDescartables]);

	const preciosPorMayorHabilitados = useMemo(() => {
		return producto.cantidadPorMayor > 0;
	}, [producto.cantidadPorMayor]);

	const preciosValidos = useMemo(() =>
		isPrecioValido(producto.precioCarta) &&
		isPrecioValido(producto.precioOnline) &&
		isPrecioValido(producto.precioPorMayor) &&
		isPrecioValido(producto.precioPorMayorOnline),
		[producto.precioCarta, producto.precioOnline, producto.precioPorMayor, producto.precioPorMayorOnline, isPrecioValido]
	);

	// Función para crear tooltip con error
	const crearTooltipError = useCallback((campo, children, mensajeError) => {
		if (!open) return children;

		const tieneError = camposConError.has(campo) && mostrarTooltips;

		if (tieneError) {
			return (
				<Tooltip
					title={mensajeError || "Este campo es obligatorio"}
					placement="top"
					open={true}
					arrow
					componentsProps={{
						tooltip: {
							sx: {
								bgcolor: 'error.main',
								'& .MuiTooltip-arrow': {
									color: 'error.main',
								},
							},
						},
					}}
				>
					{children}
				</Tooltip>
			);
		}

		return children;
	}, [open, camposConError, mostrarTooltips]);

	return (
		<Modal
			aria-labelledby="transition-modal-title"
			aria-describedby="transition-modal-description"
			open={open}
			onClose={handleClose}
			closeAfterTransition
		>
			<Fade in={open}>
				<Card sx={style}>
					<SoftBox>
						<SoftBox display="flex" justifyContent="space-between">
							<SoftBox>
								<SoftTypography variant="h6">
									{esIngrediente ? "Producto como ingrediente" : "Producto / Plato / Bebida"}
								</SoftTypography>
							</SoftBox>
							<SoftBox display="flex" justifyContent="space-between">
								<SoftBox>
									<Tooltip title="Cerrar" placement="top">
										<Icon fontSize="medium" onClick={handleClose} sx={{ cursor: "pointer" }}>
											close
										</Icon>
									</Tooltip>
								</SoftBox>
							</SoftBox>
						</SoftBox>
						{esIngrediente && (
							<Card
								sx={{
									display: "flex",
									flexDirection: "column",
									padding: 2,
									marginTop: 2,
									marginBottom: 2,
									backgroundColor: "rgba(226, 224, 203, 0.79)",
								}}
							>
								<SoftBox
									sx={{
										display: "flex",
										justifyContent: "space-between",
										alignItems: "center",
										mb: 1,
									}}
								>
									<SoftTypography variant="body2" fontWeight="bold" color="dark">
										{`Atención: el producto que estás ${selectedProducto ? "editando es" : "creando será"
											} un ingrediente de cantidad unitaria`}
									</SoftTypography>
								</SoftBox>
								<SoftTypography variant="caption" pl={0.5} color="dark">
									Esto quiere decir que, por ejemplo, si estás haciendo una base de tarta, esa base
									será suficiente para una sola tarta. Por eso, los ingredientes y sus cantidades
									deben ser los justos y necesarios para preparar una sola unidad de ese producto.
								</SoftTypography>
							</Card>
						)}
						<SoftBox py={2}>
							<Grid container spacing={1}>
								<Grid item xs={12} sm={esIngrediente ? 4 : 8}>
									<InputLabel htmlFor="nombre">
										<SoftTypography component="label" variant="caption" fontWeight="bold">
											Nombre
											<SoftTypography variant="caption" fontWeight="bold" color="primary">
												&nbsp;*
											</SoftTypography>
										</SoftTypography>
									</InputLabel>
									{crearTooltipError('nombre',
										<SoftInput
											placeholder="Ingrese el nombre"
											type="text"
											name="nombre"
											value={producto.nombre}
											onChange={handleChangeProducto}
										/>
									)}
								</Grid>

								{!esIngrediente && (
									<Grid item xs={12} sm={esIngrediente ? 3 : 4}>
										<InputLabel htmlFor="categoria">
											<SoftTypography component="label" variant="caption" fontWeight="bold">
												Categoría
												<SoftTypography variant="caption" fontWeight="bold" color="primary">
													&nbsp;*
												</SoftTypography>
											</SoftTypography>
										</InputLabel>
										{crearTooltipError('idCategoria',
											<Select
												placeholder="Seleccione una categoría"
												value={producto.idCategoria}
												onChange={handleChangeProducto}
												name="idCategoria"
											>
												{categorias.map((categoria) => (
													<MenuItem key={categoria.id} value={categoria.id}>
														{categoria.categoria}
													</MenuItem>
												))}
											</Select>
										)}
									</Grid>
								)}

								{esIngrediente && (
									<Grid item xs={12} md={3}>
										<InputLabel htmlFor="tipoUnidad">
											<SoftTypography component="label" variant="caption" fontWeight="bold">
												Tipo de unidad del producto ingrediente
												<SoftTypography variant="caption" fontWeight="bold" color="primary">
													&nbsp;*
												</SoftTypography>
											</SoftTypography>
										</InputLabel>
										{crearTooltipError('idTipoUnidad',
											<Select
												name="idTipoUnidad"
												value={producto.idTipoUnidad || ""}
												disabled={
													tipoUnidadIngredienteBloqueado ||
													verificandoBloqueoTipoUnidadIngrediente
												}
												onChange={(e) => {
													setProducto({
														...producto,
														idTipoUnidad: e.target.value,
													});
												}}
											>
												{unidades?.unidades?.map((item, index) => (
													<MenuItem key={index} value={item.id}>
														{`${item.nombre} (${item.abreviatura})`}
													</MenuItem>
												))}
											</Select>
										)}
										{verificandoBloqueoTipoUnidadIngrediente && (
											<SoftTypography variant="caption" color="secondary">
												Verificando restricciones para editar la unidad...
											</SoftTypography>
										)}
										{tipoUnidadIngredienteBloqueado && (
											<SoftTypography variant="caption" color="error">
												{motivoBloqueoTipoUnidadIngrediente}
											</SoftTypography>
										)}
									</Grid>
								)}

								{!esIngrediente && producto.idCategoria !== "16" && (
									<Grid item xs={12} sm={3}>
										<InputLabel htmlFor="nombre">
											<SoftTypography component="label" variant="caption" fontWeight="bold">
												Sección comandera
												<SoftTypography variant="caption" fontWeight="bold" color="primary">
													&nbsp;*
												</SoftTypography>
											</SoftTypography>
										</InputLabel>
										{crearTooltipError('idComanderas',
											<Select
												placeholder="Seleccione comandera"
												multiple
												value={producto.idComanderas || []}
												onChange={handleChangeProducto}
												name="idComanderas"
												disabled={esIngrediente}
												renderValue={(selected) => (
													<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
														{selected.map((idComandera) => {
															const comandera = comanderasDisponibles.find(
																(item) => String(item.id) === String(idComandera)
															);
															return (
																<Chip
																	key={`comandera-${idComandera}`}
																	label={comandera?.printName || idComandera}
																/>
															);
														})}
													</Box>
												)}
											>
												{comanderasDisponibles.map((comandera) => (
													<MenuItem key={comandera.id} value={String(comandera.id)}>
														<Box sx={{ display: "flex", alignItems: "center" }}>
															<Checkbox
																checked={(producto.idComanderas || []).indexOf(String(comandera.id)) > -1}
																sx={{ mr: 1, p: 0 }}
															/>
															{comandera.printName}
														</Box>
													</MenuItem>
												))}
											</Select>
										)}
									</Grid>
								)}

								{!esIngrediente && (
									<Grid item xs={12} sm={producto.idCategoria !== "16" ? 3 : 4}>
										<InputLabel htmlFor="nombre">
											<SoftTypography component="label" variant="caption" fontWeight="bold">
												Vender en
												<SoftTypography variant="caption" fontWeight="bold" color="primary">
													&nbsp;*
												</SoftTypography>
											</SoftTypography>
										</InputLabel>
										{crearTooltipError('lugarVenta',
											<Select
												id="lugarVenta"
												name="lugarVenta"
												multiple
												value={producto.lugarVenta || []}
												onChange={handleChangeProducto}
												fullWidth
												renderValue={(selected) => (
													<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
														{selected.map((place, index) => (
															<Chip
																key={place + index}
																label={LUGARES_VENTA.find((mov) => mov === place) || place}
															/>
														))}
													</Box>
												)}
											>
												{LUGARES_VENTA.map((mov, index) => (
													<MenuItem
														key={mov + index}
														value={mov}
														disabled={mov === "APP" && producto.idCategoria === "16"}
													>
														<Box sx={{ display: 'flex', alignItems: 'center' }}>
															<Checkbox
																checked={(producto.lugarVenta || []).indexOf(mov) > -1}
																sx={{ mr: 1, p: 0 }}
															/>
															{mov}
														</Box>
													</MenuItem>
												))}
											</Select>
										)}
									</Grid>
								)}

								<Grid item xs={12} sm={3}>
									<InputLabel htmlFor="area">
										<SoftTypography component="label" variant="caption" fontWeight="bold">
											Área
											<SoftTypography variant="caption" fontWeight="bold" color="primary">
												&nbsp;*
											</SoftTypography>
										</SoftTypography>
									</InputLabel>
									{crearTooltipError('idArea',
										<Select
											name="idArea"
											value={producto.idArea || ""}
											onChange={(e) => {
												setProducto({
													...producto,
													idArea: e.target.value,
												});
											}}
										>
											<MenuItem value="" disabled>
												Seleccione un área
											</MenuItem>
											{areas?.areaProductos?.map((area) => (
												<MenuItem key={area.id} value={area.id}>
													{area.nombre}
												</MenuItem>
											))}
										</Select>
									)}
								</Grid>

								{!esIngrediente && producto.idCategoria !== "16" && (
									<Grid item xs={12} sm={esIngrediente ? 2 : 3}>
										<InputLabel htmlFor="cantidadPorciones">
											<SoftTypography component="label" variant="caption" fontWeight="bold">
												Porciones
												<SoftTypography variant="caption" fontWeight="bold" color="primary">
													&nbsp;*
												</SoftTypography>
											</SoftTypography>
										</InputLabel>
										{crearTooltipError('cantidadPorciones',
											<SoftInput
												placeholder="Ingrese la cantidad"
												type="number"
												name="cantidadPorciones"
												value={producto.cantidadPorciones}
												onChange={handleChangeProducto}
											/>
										)}
									</Grid>
								)}

								{!esIngrediente && producto.idCategoria !== "16" && (
									<Grid item sx={4} sm={3}>
										<InputLabel htmlFor="precioCarta">
											<SoftTypography component="label" variant="caption" fontWeight="bold">
												Precio por porción
												<SoftTypography variant="caption" fontWeight="bold" color="primary">
													&nbsp;*
												</SoftTypography>
											</SoftTypography>
										</InputLabel>
										{crearTooltipError('precioCarta',
											<div>
												<SoftInputNumber
													label="precioCarta"
													placeholder="Ingrese el precio"
													icon={{
														component: <Icon color="primary">attach_money</Icon>,
														direction: "left",
													}}
													name="precioCarta"
													value={producto.precioCarta}
													onChange={esIngrediente ? null : (e) => handleChangeProducto(e)}
													disabled={esIngrediente}
												/>
											</div>
										)}
									</Grid>
								)}

								{!esIngrediente && producto.idCategoria !== "16" && (
									<Grid item sx={4} sm={3}>
										<InputLabel htmlFor="precioOnline">
											<SoftTypography component="label" variant="caption" fontWeight="bold">
												Precio por porción para llevar
												<SoftTypography variant="caption" fontWeight="bold" color="primary">
													&nbsp;*
												</SoftTypography>
											</SoftTypography>
										</InputLabel>
										{crearTooltipError('precioOnline',
											<div>
												<SoftInputNumber
													label="precioOnline"
													placeholder="Ingrese el precio"
													icon={{
														component: <Icon color="primary">attach_money</Icon>,
														direction: "left",
													}}
													name="precioOnline"
													value={producto.precioOnline}
													onChange={esIngrediente ? null : (e) => handleChangeProducto(e)}
													disabled={esIngrediente}
												/>
											</div>
										)}
									</Grid>
								)}

								{!esIngrediente && producto.idCategoria !== "16" && (
									<Grid item xs={3} sm={3}>
										<InputLabel htmlFor="cantidadPorMayor">
											<SoftTypography component="label" variant="caption" fontWeight="bold">
												Mínimo para precio por mayor
											</SoftTypography>
										</InputLabel>
										<SoftInput
											label="cantidadPorMayor"
											placeholder="Ingrese la cantidad"
											type="number"
											name="cantidadPorMayor"
											value={producto.cantidadPorMayor}
											onChange={esIngrediente ? null : (e) => handleChangeProducto(e)}
											disabled={esIngrediente}
										/>
									</Grid>
								)}

								{!esIngrediente && producto.idCategoria !== "16" && (
									<Grid item sx={4} sm={3}>
										<InputLabel htmlFor="precioPorMayor">
											<SoftTypography component="label" variant="caption" fontWeight="bold">
												Precio por Mayor
												{preciosPorMayorHabilitados && (
													<SoftTypography variant="caption" fontWeight="bold" color="primary">
														&nbsp;*
													</SoftTypography>
												)}
											</SoftTypography>
										</InputLabel>
										{preciosPorMayorHabilitados ? crearTooltipError('precioPorMayor',
											<div>
												<SoftInputNumber
													label="precioPorMayor"
													placeholder="Ingrese el precio"
													icon={{
														component: <Icon color="primary">attach_money</Icon>,
														direction: "left",
													}}
													name="precioPorMayor"
													value={producto.precioPorMayor}
													onChange={esIngrediente ? null : (e) => handleChangeProducto(e)}
													disabled={esIngrediente || !preciosPorMayorHabilitados}
												/>
											</div>
										) : (
											<div>
												<SoftInputNumber
													label="precioPorMayor"
													placeholder="Ingrese el precio"
													icon={{
														component: <Icon color="primary">attach_money</Icon>,
														direction: "left",
													}}
													name="precioPorMayor"
													value={producto.precioPorMayor}
													onChange={esIngrediente ? null : (e) => handleChangeProducto(e)}
													disabled={esIngrediente || !preciosPorMayorHabilitados}
												/>
											</div>
										)}
									</Grid>
								)}

								{!esIngrediente && producto.idCategoria !== "16" && (
									<Grid item sx={4} sm={3}>
										<InputLabel htmlFor="precioPorMayorOnline">
											<SoftTypography component="label" variant="caption" fontWeight="bold">
												Precio por Mayor para llevar
												{preciosPorMayorHabilitados && (
													<SoftTypography variant="caption" fontWeight="bold" color="primary">
														&nbsp;*
													</SoftTypography>
												)}
											</SoftTypography>
										</InputLabel>
										{preciosPorMayorHabilitados ? crearTooltipError('precioPorMayorOnline',
											<div>
												<SoftInputNumber
													label="precioPorMayorOnline"
													placeholder="Ingrese el precio"
													icon={{
														component: <Icon color="primary">attach_money</Icon>,
														direction: "left",
													}}
													name="precioPorMayorOnline"
													value={producto.precioPorMayorOnline}
													onChange={esIngrediente ? null : (e) => handleChangeProducto(e)}
													disabled={esIngrediente || !preciosPorMayorHabilitados}
												/>
											</div>
										) : (
											<div>
												<SoftInputNumber
													label="precioPorMayorOnline"
													placeholder="Ingrese el precio"
													icon={{
														component: <Icon color="primary">attach_money</Icon>,
														direction: "left",
													}}
													name="precioPorMayorOnline"
													value={producto.precioPorMayorOnline}
													onChange={esIngrediente ? null : (e) => handleChangeProducto(e)}
													disabled={esIngrediente || !preciosPorMayorHabilitados}
												/>
											</div>
										)}
									</Grid>
								)}

								<Grid item xs={12} sm={12}>
									<InputLabel htmlFor="descripcion">
										<SoftTypography component="label" variant="caption" fontWeight="bold">
											Descripción
										</SoftTypography>
									</InputLabel>
									<SoftInput
										label="descripcion"
										placeholder="Descripción del producto"
										type="text"
										name="descripcion"
										multiline
										rows={2}
										value={producto.descripcion}
										onChange={handleChangeProducto}
									/>
								</Grid>

								<Grid item xs={12} sm={12}>
									<InputLabel htmlFor="ingredientes">
										<SoftTypography component="label" variant="subtitle2" fontWeight="bold">
											Receta
										</SoftTypography>
									</InputLabel>
								</Grid>

								<Grid item xs={12} sm={6}>
									<InputLabel htmlFor="ingredientes">
										<SoftTypography component="label" variant="caption" fontWeight="bold">
											Ingrediente
											<SoftTypography variant="caption" fontWeight="bold" color="primary">
												&nbsp;*
											</SoftTypography>
										</SoftTypography>
									</InputLabel>
									<Autocomplete
										value={materiaPrima}
										options={consumibles || []}
										getOptionLabel={(option) => option.nombre || ""}
										noOptionsText="No hay resultados"
										onInputChange={handleSearchChange}
										loading={loading}
										loadingText="Cargando..."
										onChange={(e, newValue) => {
											if (newValue) {
												setMateriaPrima({
													...materiaPrima,
													...newValue,
													idMateriaPrima: newValue?.id || "",
													idTipoUnidad: newValue?.tipoUnidad?.id || "",
													unidadOriginal: newValue?.tipoUnidad,
												});
											} else {
												setMateriaPrima({
													idMateriaPrima: "",
													cantidad: "",
												});
											}
										}}
										renderInput={(params) => (
											<TextField {...params} placeholder="Buscar ingredientes" />
										)}
									/>
								</Grid>

								<Grid item xs={12} sm={2}>
									<InputLabel htmlFor="ingredientes">
										<SoftTypography component="label" variant="caption" fontWeight="bold">
											{`Cantidad`}
										</SoftTypography>
									</InputLabel>
									<SoftInput
										placeholder="Ingrese la cantidad"
										type="number"
										name="cantidad"
										disabled={materiaPrima.idMateriaPrima === ""}
										value={materiaPrima.cantidad}
										onChange={(e) => {
											setMateriaPrima({
												...materiaPrima,
												cantidad: e.target.value,
											});
										}}
									/>
								</Grid>

								<Grid item xs={12} md={3}>
									<InputLabel htmlFor="tipoUnidad">
										<SoftTypography component="label" variant="caption" fontWeight="bold">
											Tipo de unidad
										</SoftTypography>
									</InputLabel>
									<Select
										name="idTipoUnidad"
										value={materiaPrima?.idTipoUnidad}
										onChange={(e) => {
											setMateriaPrima({
												...materiaPrima,
												idTipoUnidad: e.target.value,
												tipoUnidad: unidades?.unidades?.find(
													(unidad) => unidad.id === e.target.value
												),
												tipoUnidadOrigen: materiaPrima.unidadOriginal,
											});
										}}
									>
										{unidadesFiltradas?.map((item, index) => (
											<MenuItem key={index} value={item.id}>
												{`${item.nombre} (${item.abreviatura})`}
											</MenuItem>
										))}
									</Select>
								</Grid>

								<Grid
									item
									xs={12}
									sm={1}
									display="flex"
									justifyContent="flex-end"
									alignItems="flex-end"
								>
									<SoftButton
										fullWidth
										color="primary"
										onClick={handleAddMateriaPrima}
										disabled={materiaPrima.idMateriaPrima === "" || materiaPrima.cantidad === ""}
									>
										+
									</SoftButton>
								</Grid>

								<Grid
									item
									xs={12}
									sx={{
										maxHeight: "250px",
										overflowY: "auto",
									}}
								>
									{loading ? (
										<CircularProgress color="inherit" size={17} />
									) : listadoIngredientes.length > 0 ? (
										<DataTable columns={columns} rows={allRows} />
									) : (
										crearTooltipError('ingredientes',
											<div>
												<SinDatos
													titulo=""
													mensaje="Agregue ingredientes para la receta"
													icono="receipt"
												/>
											</div>,
											"Debe agregar al menos un ingrediente"
										)
									)}
								</Grid>

								{/* DESCARTABLES */}
								<Grid item xs={12} sm={12}>
									<InputLabel htmlFor="ingredientes">
										<SoftTypography component="label" variant="subtitle2" fontWeight="bold">
											Descartables
										</SoftTypography>
									</InputLabel>
								</Grid>
								<Grid item xs={12} sm={6}>
									<InputLabel htmlFor="descartables">
										<SoftTypography component="label" variant="caption" fontWeight="bold">
											Descartable
										</SoftTypography>
									</InputLabel>
									<Autocomplete
										value={descartable}
										options={materiales || []}
										getOptionLabel={(option) => option.nombre || ""}
										noOptionsText="No hay resultados"
										onInputChange={handleSearchChange}
										loading={loading}
										loadingText="Cargando..."
										onChange={(e, newValue) => {
											if (newValue) {
												setDescartable({
													...descartable,
													...newValue,
													idMateriaPrima: newValue?.id || "",
													idTipoUnidad: newValue?.tipoUnidad?.id || "",
												});
											} else {
												setDescartable({
													idMateriaPrima: "",
													cantidad: "",
												});
											}
										}}
										renderInput={(params) => (
											<TextField {...params} placeholder="Buscar ingredientes" />
										)}
									/>
								</Grid>
								<Grid item xs={12} sm={2}>
									<InputLabel htmlFor="ingredientes">
										<SoftTypography component="label" variant="caption" fontWeight="bold">
											{`Cantidad`}
										</SoftTypography>
									</InputLabel>
									<SoftInput
										placeholder="Ingrese la cantidad"
										type="number"
										name="cantidad"
										disabled={descartable.idMateriaPrima === ""}
										value={descartable.cantidad}
										onChange={(e) => {
											setDescartable({
												...descartable,
												cantidad: e.target.value,
											});
										}}
									/>
								</Grid>
								<Grid item xs={12} md={3}>
									<InputLabel htmlFor="tipoUnidad">
										<SoftTypography component="label" variant="caption" fontWeight="bold">
											Tipo de unidad
										</SoftTypography>
									</InputLabel>
									<Select
										name="idTipoUnidad"
										value={descartable?.idTipoUnidad}
										// onChange={handleChange}
										onChange={(e) => {
											setDescartable({
												...descartable,
												idTipoUnidad: e.target.value,
												tipoUnidad: unidades?.unidades?.find(
													(unidad) => unidad.id === e.target.value
												),
												tipoUnidadOrigen:
													materiales.find((item) => item.id === descartable.idMateriaPrima)
														?.tipoUnidad ||
													unidades?.unidades?.find((unidad) => unidad.id === e.target.value),
											});
										}}
									>
										{unidades?.unidades?.map((item, index) => (
											<MenuItem key={index} value={item.id}>
												{`${item.nombre} (${item.abreviatura})`}
											</MenuItem>
										))}
									</Select>
								</Grid>
								<Grid
									item
									xs={12}
									sm={1}
									display="flex"
									justifyContent="flex-end"
									alignItems="flex-end"
								>
									<SoftButton
										fullWidth
										color="primary"
										onClick={handleAddDescartable}
									// disabled={materiaPrima.idMateriaPrima === "" || materiaPrima.cantidad === ""}
									>
										+
									</SoftButton>
								</Grid>
								<Grid
									item
									xs={12}
									sx={{
										maxHeight: "250px",
										overflowY: "auto",
									}}
								>
									{loading ? (
										<CircularProgress color="inherit" size={17} />
									) : listadoDescartables?.length > 0 ? (
										<DataTable columns={columnsDescartables} rows={allRowsDescartables} />
									) : (
										<SinDatos
											titulo=""
											mensaje="Agregue descartables para el producto"
											icono="receipt"
										/>
									)}
								</Grid>
							</Grid>
						</SoftBox>
						<SoftBox
							display={{
								xs: "flex-row",
								sm: "flex",
							}}
							justifyContent="space-between"
							alignItems="center"
						>
							{
								crearTooltipError(
									'costosuperior',
									<SoftBox display="flex" flexDirection="column">
										<SoftTypography component="label" variant="caption" fontWeight="bold">
											Costo total por unidad{' '}
											<Tooltip
												title={`Ingredientes: $${formatMoneyPunto(costoPorPorcion)} + Descartables: $${formatMoneyPunto(costoDescartablesPorUnidad)}`}
												placement="top"
											>
												<Icon sx={{ fontSize: 16, verticalAlign: 'middle', color: getUproColor('primary') }}>info</Icon>
											</Tooltip>
										</SoftTypography>
										<SoftTypography component="label" variant="subtitle2" fontWeight="bold">
											{`$${formatMoneyPunto(costoTotalPorUnidad)}`}
										</SoftTypography>
									</SoftBox>,
									"El costo de ingredientes no puede ser mayor o igual al precio de venta"
								)
							}
							<SoftBox
								display={{
									xs: "flex-row",
									sm: "flex",
								}}
								justifyContent="end"
								alignItems="center"
							>
								<SoftBox
									mr={{
										xs: 0,
										sm: 2,
									}}
									mt={{
										xs: 2,
										sm: 0,
									}}
								>
									<SoftButton
										color="primary"
										circular
										onClick={handleClose}
										fullWidth={{
											xs: true,
											sm: false,
										}}
										disabled={loadingSave}
									>
										<Icon sx={{ fontWeight: "light" }}>cancel</Icon>
										&nbsp;Cancelar
									</SoftButton>
								</SoftBox>
								<SoftBox
									mt={{
										xs: 2,
										sm: 0,
									}}
								>
									<SoftButton
										color="uproGreen"
										circular
										fullWidth={{
											xs: true,
											sm: false,
										}}
										onClick={handleSave}
										disabled={loadingSave}
									>
										{false ? (
											<CircularProgress size={15} color="inherit" />
										) : (
											<Icon sx={{ fontWeight: "light" }}>save</Icon>
										)}
										&nbsp;Guardar
									</SoftButton>
								</SoftBox>
							</SoftBox>
						</SoftBox>
						<ModalNuevoTipo
							open={modalNuevoTipo}
							handleClose={() => {
								setModalNuevoTipo(false);
							}}
							refetchTipo={refetchTipos}
						/>
					</SoftBox>
				</Card>
			</Fade>
		</Modal>
	);
}

ModalProducto.propTypes = {
	open: PropTypes.bool,
	handleClose: PropTypes.func,
	refetch: PropTypes.func,
	selectedProducto: PropTypes.object,
	categorias: PropTypes.array,
	tiposProducto: PropTypes.array,
	refetchTipos: PropTypes.func,
};

